const FALLBACK_COLORS = [
	'#0897C1',
	'#12a36f',
	'#2aa198',
	'#f2a93b',
	'#ef6f88',
	'#9c6ade',
	'#5bb1e3',
	'#8bc34a',
	'#ff8a65',
	'#26c6da',
];

const DYNAMIC_TOP_COMPETITORS_LIMIT = 5;

const pad = (value) => String(value).padStart(2, '0');

export const normalizeCompetitorName = (value) => {
	const raw = String(value || '').trim();
	if (!raw) {
		return '';
	}

	const cleaned = raw
		.replace(/^https?:\/\//i, '')
		.replace(/^www\./i, '')
		.split('/')[0]
		.split('?')[0]
		.split('#')[0]
		.split(':')[0]
		.trim();

	let baseName = cleaned;
	if (cleaned && !cleaned.includes(' ') && cleaned.includes('.')) {
		const parts = cleaned
			.toLowerCase()
			.split('.')
			.map(part => part.trim())
			.filter(Boolean);
		const rootLikeParts = new Set(['co', 'com', 'net', 'org', 'gov', 'edu']);

		if (parts.length >= 3 && parts[parts.length - 1].length === 2 && rootLikeParts.has(parts[parts.length - 2])) {
			baseName = parts[parts.length - 3];
		}
		else if (parts.length >= 2) {
			baseName = parts[parts.length - 2];
		}
		else {
			baseName = parts[0] || cleaned;
		}
	}

	return String(baseName || raw)
		.split(' ')
		.map(word => word.split('-').map(part => {
			if (!part) return part;
			return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
		}).join('-'))
		.join(' ')
		.toLowerCase();
};

const getShareMaps = (scoreBucket = {}) => {
	const byActor = {};
	const byName = {};

	// byActor is the authoritative source (matches geo_scores' share_of_voice_by_actor
	// / visibility_score). Do NOT bump it using share_of_voice_full_list cluster
	// shares: the two metrics are rounded differently (2 vs 4 decimals) and the
	// cluster-level share can diverge from the actor-level share for a same actor.
	const byActorRaw = scoreBucket?.share_of_voice_by_actor || {};
	Object.entries(byActorRaw).forEach(([actorId, share]) => {
		byActor[Number(actorId)] = Number(share || 0);
	});

	const fullList = Array.isArray(scoreBucket?.share_of_voice_full_list) ? scoreBucket.share_of_voice_full_list : [];
	fullList.forEach((row) => {
		const share = Number(row?.share || 0);

		// byName is only used as a fallback for dynamic descriptors that don't
		// map to a known actor_id.
		const normalizedName = normalizeCompetitorName(row?.name);
		if (normalizedName) {
			byName[normalizedName] = Math.max(Number(byName[normalizedName] || 0), share);
		}
	});

	return { byActor, byName, fullList };
};

const resolveActorId = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const actorId = Number(value);
	return Number.isFinite(actorId) ? actorId : null;
};

const buildActorMetaIndex = (actors = []) => {
	const list = [];
	const byId = new Map();

	(actors || []).forEach((actor, index) => {
		const actorId = resolveActorId(actor?.id);
		const aliases = new Set([
			normalizeCompetitorName(actor?.name),
			normalizeCompetitorName(actor?.url),
		].filter(Boolean));
		const meta = {
			actor_id: actorId,
			name: actorId === 0 ? 'Your visibility' : String(actor?.url || actor?.name || `Actor ${actorId ?? index}`),
			color: actor?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
			aliases: Array.from(aliases),
		};

		list.push(meta);
		if (actorId !== null) {
			byId.set(actorId, meta);
		}
	});

	return { list, byId };
};

const resolveActorMetaFromCandidates = ({ actorIndex, actorId = null, candidates = [], fallbackName = 'Unknown', fallbackColorIndex = 0 }) => {
	if (actorId !== null && actorIndex.byId.has(actorId)) {
		return actorIndex.byId.get(actorId);
	}

	const normalizedCandidates = candidates.map(candidate => normalizeCompetitorName(candidate)).filter(Boolean);
	const matchedMeta = actorIndex.list.find(meta =>
		normalizedCandidates.some(candidate => meta.aliases.includes(candidate))
	);

	if (matchedMeta) {
		return matchedMeta;
	}

	return {
		actor_id: actorId,
		name: fallbackName,
		color: FALLBACK_COLORS[fallbackColorIndex % FALLBACK_COLORS.length],
		aliases: normalizedCandidates,
	};
};

const getSortedFullList = (scoreBucket = {}) => {
	const { fullList } = getShareMaps(scoreBucket);

	return [...fullList].sort((left, right) => {
		const mentionCountDiff = Number(right?.mention_count || 0) - Number(left?.mention_count || 0);
		if (mentionCountDiff !== 0) {
			return mentionCountDiff;
		}

		const shareDiff = Number(right?.share || 0) - Number(left?.share || 0);
		if (shareDiff !== 0) {
			return shareDiff;
		}

		return String(left?.name || '').localeCompare(String(right?.name || ''));
	});
};

export const shouldUseDynamicTopCompetitors = ({ competitorLabelId = null, labelIds = [] } = {}) => {
	return Array.isArray(labelIds) && labelIds.length > 0 && (competitorLabelId === null || competitorLabelId === undefined || competitorLabelId === '');
};

const buildSeriesDescriptorFromMeta = (meta = {}) => {
	const actorId = resolveActorId(meta?.actor_id);
	const aliases = Array.isArray(meta?.aliases) ? meta.aliases.filter(Boolean) : [];

	return {
		actor_id: actorId,
		name: actorId === 0 ? 'Your visibility' : String(meta?.name || 'Unknown'),
		color: meta?.color || FALLBACK_COLORS[0],
		aliases,
	};
};

const buildSeriesDescriptorFromRow = ({ row = {}, actorIndex, colorIndex = 0 }) => {
	const actorId = resolveActorId(row?.actor_id);
	const resolvedMeta = resolveActorMetaFromCandidates({
		actorIndex,
		actorId,
		candidates: [row?.key, row?.name],
		fallbackName: String(row?.name || 'Unknown'),
		fallbackColorIndex: colorIndex,
	});
	const aliases = new Set([
		...(resolvedMeta?.aliases || []),
		normalizeCompetitorName(row?.key),
		normalizeCompetitorName(row?.name),
	]);

	return {
		actor_id: actorId,
		name: actorId === 0 ? 'Your visibility' : String(row?.name || resolvedMeta?.name || 'Unknown'),
		color: resolvedMeta?.color || FALLBACK_COLORS[colorIndex % FALLBACK_COLORS.length],
		aliases: Array.from(aliases).filter(Boolean),
	};
};

const buildTrackedDescriptors = ({ actors = [], scoreBucket = {} }) => {
	const actorIndex = buildActorMetaIndex(actors);

	if (actorIndex.list.length > 0) {
		return actorIndex.list.map(meta => buildSeriesDescriptorFromMeta(meta));
	}

	return getSortedFullList(scoreBucket).map((row, index) => buildSeriesDescriptorFromRow({
		row,
		actorIndex,
		colorIndex: index,
	}));
};

const buildDynamicDescriptors = ({ actors = [], scoreBucket = {}, topN = DYNAMIC_TOP_COMPETITORS_LIMIT }) => {
	const actorIndex = buildActorMetaIndex(actors);
	const sortedRows = getSortedFullList(scoreBucket);
	const clientRow = sortedRows.find(row => resolveActorId(row?.actor_id) === 0 || row?.is_client === true);
	const nonClientRows = sortedRows.filter(row => resolveActorId(row?.actor_id) !== 0 && row?.is_client !== true && row?.is_other !== true);
	const descriptors = [];
	const { byActor } = getShareMaps(scoreBucket);
	const clientShare = Number(byActor[0] || 0);

	if (!clientRow && clientShare <= 0 && nonClientRows.length === 0) {
		return [];
	}

	const clientMeta = resolveActorMetaFromCandidates({
		actorIndex,
		actorId: 0,
		candidates: [clientRow?.key, clientRow?.name],
		fallbackName: 'Your visibility',
		fallbackColorIndex: 0,
	});
	descriptors.push(buildSeriesDescriptorFromMeta({
		...clientMeta,
		actor_id: 0,
		name: 'Your visibility',
	}));

	nonClientRows.slice(0, topN).forEach((row, index) => {
		descriptors.push(buildSeriesDescriptorFromRow({
			row,
			actorIndex,
			colorIndex: index + 1,
		}));
	});

	return descriptors;
};

const buildSeriesDescriptors = ({
	actors = [],
	scoreBucket = {},
	useDynamicTopCompetitors = false,
	dynamicTopN = DYNAMIC_TOP_COMPETITORS_LIMIT,
}) => {
	return useDynamicTopCompetitors
		? buildDynamicDescriptors({ actors, scoreBucket, topN: dynamicTopN })
		: buildTrackedDescriptors({ actors, scoreBucket });
};

const resolveDescriptorShare = ({ scoreBucket = {}, descriptor = {} }) => {
	const { byActor, byName } = getShareMaps(scoreBucket);
	const actorId = resolveActorId(descriptor?.actor_id);

	// When the descriptor maps to a known actor_id, use share_of_voice_by_actor
	// as the single source of truth (mirrors geo_scores' visibility_score).
	if (actorId !== null) {
		return Number(byActor[actorId] || 0);
	}

	// Fallback for dynamic cluster descriptors without a resolved actor_id:
	// rely on cluster-level share matched via aliases.
	const aliases = Array.isArray(descriptor?.aliases) ? descriptor.aliases : [];
	let share = 0;
	aliases.forEach((alias) => {
		share = Math.max(share, Number(byName[alias] || 0));
	});

	return share;
};

export const resolveTimestamp = (endDate) => {
	if (endDate instanceof Date && !Number.isNaN(endDate.getTime())) {
		return endDate.getTime();
	}

	if (typeof endDate === 'number' && Number.isFinite(endDate)) {
		return endDate > 9999999999 ? endDate : endDate * 1000;
	}

	if (typeof endDate === 'string') {
		const trimmed = endDate.trim();
		const ddmmyyyy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
		if (ddmmyyyy) {
			const [, day, month, year] = ddmmyyyy;
			const parsed = new Date(`${year}-${month}-${day}T16:00:00Z`);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed.getTime();
			}
		}

		const parsed = new Date(trimmed);
		if (!Number.isNaN(parsed.getTime())) {
			return parsed.getTime();
		}
	}

	return Date.now();
};

export const toIsoDayString = (value) => {
	const timestamp = resolveTimestamp(value);
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const listSnapshotDatesInRange = (dates = [], rangeStart = null, rangeEnd = null) => {
	const startDayKey = toIsoDayString(rangeStart);
	const endDayKey = toIsoDayString(rangeEnd);

	if (!startDayKey || !endDayKey) {
		return [];
	}

	const minDayKey = startDayKey <= endDayKey ? startDayKey : endDayKey;
	const maxDayKey = startDayKey <= endDayKey ? endDayKey : startDayKey;
	const uniqueByDayKey = new Map();

	(dates || []).forEach((entry) => {
		const dayKey = toIsoDayString(entry);
		if (!dayKey || dayKey < minDayKey || dayKey > maxDayKey) {
			return;
		}

		const timestamp = resolveTimestamp(entry);
		const current = uniqueByDayKey.get(dayKey);
		if (!current || timestamp > current.timestamp) {
			uniqueByDayKey.set(dayKey, {
				dayKey,
				timestamp,
			});
		}
	});

	return Array.from(uniqueByDayKey.values()).sort((left, right) => left.timestamp - right.timestamp);
};

export const buildCompetitiveLandscapeSeries = ({
	actors = [],
	scoreBucket = {},
	endDate = null,
	useDynamicTopCompetitors = false,
	dynamicTopN = DYNAMIC_TOP_COMPETITORS_LIMIT,
}) => {
	const timestamp = resolveTimestamp(endDate);
	const descriptors = buildSeriesDescriptors({
		actors,
		scoreBucket,
		useDynamicTopCompetitors,
		dynamicTopN,
	});

	return descriptors.map(descriptor => ({
		actor_id: descriptor.actor_id,
		name: descriptor.name,
		color: descriptor.color,
		data: [[timestamp, Math.round(resolveDescriptorShare({ scoreBucket, descriptor }) * 100)]],
	}));
};

export const buildCompetitiveLandscapeEvolutionSeries = ({
	actors = [],
	snapshots = [],
	useDynamicTopCompetitors = false,
	dynamicTopN = DYNAMIC_TOP_COMPETITORS_LIMIT,
}) => {
	const sortedSnapshots = [...(snapshots || [])].sort((left, right) => resolveTimestamp(left?.timestamp ?? left?.endDate ?? null) - resolveTimestamp(right?.timestamp ?? right?.endDate ?? null));
	const latestSnapshot = sortedSnapshots[sortedSnapshots.length - 1];
	const descriptors = buildSeriesDescriptors({
		actors,
		scoreBucket: latestSnapshot?.scoreBucket || {},
		useDynamicTopCompetitors,
		dynamicTopN,
	});

	return descriptors.map((descriptor) => ({
		actor_id: descriptor.actor_id,
		name: descriptor.name,
		color: descriptor.color,
		data: sortedSnapshots.map((snapshot) => [
			resolveTimestamp(snapshot?.timestamp ?? snapshot?.endDate ?? null),
			Math.round(resolveDescriptorShare({
				scoreBucket: snapshot?.scoreBucket || {},
				descriptor,
			}) * 100),
		]),
	}));
};
